/**
 * «Spedito» su tanti ordini insieme, dalla lista.
 *
 * È il comando con cui si spedisce davvero: seleziona, Spedito, Applica. Sotto
 * il backend manda gli Amazon in UN feed solo e conferma Mirakl/WooCommerce
 * uno per uno, ma per chi guarda è un gesto solo.
 *
 * ⚠️ Manda la mail di spedizione a clienti VERI, a lotti.
 */

import { apiPost } from "@/shared/api";

/** Lo stato che significa «spedisci»: è il default della tendina. */
export const STATO_SPEDITO = "FULL_SHIPPED";

export type EsitoOrdine = {
  reference: string;
  confermato: boolean;
  inviato: boolean;
  gia_fatto: boolean;
  motivo: string;
  feed_id?: string;
};

export type ConfermaBulkResult = {
  esiti: EsitoOrdine[];
  confermati: number;
  inviati: number;
  falliti: number;
};

export function confermaSpedizioneBulk(orderRefs: string[]): Promise<ConfermaBulkResult> {
  return apiPost<ConfermaBulkResult>("/api/orders/bulk-conferma-spedizione", {
    order_refs: orderRefs,
  });
}

/** Quanti falliti nominare per esteso prima di passare al conteggio. */
const DA_NOMINARE = 3;

/**
 * Il riepilogo da mostrare all'operatore.
 *
 * ⚠️ I totali da soli non bastano: «18 su 20» non dice a quale cliente manca
 * il tracking. Chi è rimasto indietro va nominato, con il motivo — è l'unica
 * informazione che permette di rimediare.
 */
export function riepilogoConfermaBulk(result: ConfermaBulkResult): {
  tono: "ok" | "errore";
  testo: string;
} {
  const { esiti, confermati, inviati } = result;
  if (esiti.length === 0) {
    return { tono: "ok", testo: "Nessun ordine da confermare." };
  }

  const falliti = esiti.filter((e) => !e.confermato && !e.inviato && !e.gia_fatto);
  const gia = esiti.filter((e) => e.gia_fatto).length;

  const fatti: string[] = [];
  if (confermati) fatti.push(`${confermati} confermat${confermati === 1 ? "o" : "i"}`);
  // Amazon: preso in carico, non ancora spedito. Tenerli separati è il punto.
  if (inviati) fatti.push(`${inviati} inviat${inviati === 1 ? "o" : "i"} ad Amazon`);
  if (gia) fatti.push(`${gia} già a posto`);

  if (falliti.length === 0) {
    return {
      tono: "ok",
      testo: fatti.length ? `${fatti.join(", ")}.` : "Niente da fare: erano già tutti a posto.",
    };
  }

  const nominati = falliti
    .slice(0, DA_NOMINARE)
    .map((e) => `${e.reference} (${e.motivo || "motivo non riportato"})`)
    .join("; ");
  const resto = falliti.length - DA_NOMINARE;
  const coda = resto > 0 ? ` e altri ${resto}` : "";
  const prima = fatti.length ? `${fatti.join(", ")}. ` : "";
  return {
    tono: "errore",
    testo: `${prima}${falliti.length} NON confermat${falliti.length === 1 ? "o" : "i"}: ${nominati}${coda}.`,
  };
}
